import { Button, CIActionBtnGroup, MyIcon } from 'ccs-ui-components';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useConfirmDialog from '../../../../../../common/hooks/ConfirmationDialog';
import SquareBadge from '../../../../../_components/badges/SquareBadge';
import { FORM_SERVICE_TYPE } from '../../../../../constants';
import { AppDispatch, useSelector } from '../../../../../store/AppStore';
import { deleteEgressEventMapping } from '../../../../../store/reducer/services/EgressEventMappingReducer';
import { deleteIngressEventMapping } from '../../../../../store/reducer/services/IngressEventMappingReducer';
import { deleteServiceOperations } from '../../../../../store/reducer/services/ServiceOperationReducer';
import { getOperationVerbLabel } from '../../../../../utilities/services/LabelUtils';
import {
  getAssociatedEgressEvents,
  getAssociatedIngressEvents
} from '../../../../../utilities/services/ServiceEventAssociationUtils';
import { isDeleteServiceOperationDisabled } from '../../../../../utilities/services/ServiceListUtils';
import classes from './ServiceOperationList.module.scss';

interface IServiceOpsWrapperProps {
  onAddHandler: () => void;
  onEditHandler: (index: number) => void;
  onViewHandler: (index: number) => void;
  isEditService?: boolean;
  isServiceOpsEditButtonClicked?: boolean;
  isViewMode: boolean
}

const ServiceOperationList = ({ onAddHandler, onEditHandler, onViewHandler, isEditService = false, isServiceOpsEditButtonClicked = false, isViewMode }: IServiceOpsWrapperProps) => {
  /* Get Data from the store */
  const { serviceOperations } = useSelector((state) => state.serviceOperationReducer);
  const { serviceType } = useSelector((state) => state.serviceDetailsReducer);
  const { ingressEventMappingList } = useSelector((state) => state.ingressEventMappingReducer);
  const { egressEventMappingList } = useSelector((state) => state.egressEventMappingReducer);
  /* Hooks */
  const dispatch = useDispatch<AppDispatch>();
  const { t: translate } = useTranslation();
  const useConfirmDialogHook = useConfirmDialog();

  /**
   * @method onEditClickHandler
   * @description calls edit
   * @param index  edit index
   */
  const onEditClickHandler = (index: number) => {
    onEditHandler(index);
  };

  /**
   * @name confirmDeleteServiceOperation
   * @description: Method will delete the Service Operation after user confirmation
   * @param index
   */
  const confirmDeleteServiceOperation = (index: number) => {
    useConfirmDialogHook.show({ title: translate('confirm-dialog-prompt-box.title'), message: translate('delete-service-operation.message'), cancelText: translate('confirm-dialog-prompt-box.lbl_close'), okText: translate('confirm-dialog-prompt-box.lbl_confirm') }).then((cnfStatus) => {
      if (cnfStatus) {
        deleteServiceOperation(index);
      }
    });
  };
  /**
   * @method deleteServiceOperation
   * @description Deletes the service operations
   * @param index deletion index
   */
  const deleteServiceOperation = (index: number) => {
    const serviceOperationToBeDeleted = serviceOperations[index];
    dispatch(
      deleteServiceOperations({ indexNumber: index, statusMsg: 'deleted' })
    );

    const ingressEventToBeDeleted = ingressEventMappingList.filter(
      (so1) => serviceOperationToBeDeleted.serviceOperation === so1.serviceOperationName
    );
    // delete associated ingress event
    if (ingressEventToBeDeleted.length) {
      ingressEventToBeDeleted.forEach((ingress) => {
        dispatch(
          deleteIngressEventMapping({
            serviceOperationName: ingress.serviceOperationName,
            ingressEventName: ingress.eventName,
            ingressEventVersion: ingress.eventVersion

          })
        );
      });
    }
    const egressEventToBeDeleted = egressEventMappingList.filter(
      (so1) => serviceOperationToBeDeleted.serviceOperation === so1.serviceOperationName
    );
    egressEventToBeDeleted.forEach((egress) => {
      dispatch(deleteEgressEventMapping(
        { serviceOperationName: egress.serviceOperationName,
          eventName: egress.eventName,
          ingressName: egress.ingressName,
          eventVersion: egress.eventVersion,
          ingressEventVersion: egress.ingressEventVersion
        }
      ));
    });
  };
  return (
    <div className={classes['service-ops-list-container']}>
      {!isServiceOpsEditButtonClicked && (
        <div>
          <Row className={classes.btn}>
            <Button
              className={classes['add-service-ops-btn']}
              variant="secondary"
              onClick={onAddHandler}
              data-testid="btn-add-new-service-operation"
              disabled={isViewMode}
            >
              {translate('service_operation_list.add_service_operation_btn')}
            </Button>
          </Row>
          <div>
            <Row className={classes['service-ops-list-labels']}>
              <Col sm={3} md={3} lg={3}>
                {translate('service_operation_list.lbl_service_operation')}
              </Col>
              <Col sm={3} md={3} lg={3}>
                {getOperationVerbLabel(serviceType, translate)}
              </Col>
              <Col sm={2} md={2} lg={2}>
                {translate('service_operation_list.lbl_ingress_events')}
              </Col>
              <Col sm={2} md={2} lg={2}>
                {translate('service_operation_list.lbl_engress_events')}
              </Col>
              <Col sm={2} md={2} lg={2} />
            </Row>
            {serviceOperations.length < 1 && (
              <Row>
                <div className={classes['service-ops-list-data']}>
                  {translate('service_operation_list.no_records')}
                </div>
              </Row>
            )}
            {serviceOperations.length > 0
              && serviceOperations.map((item, index) => (
                <Row
                  className={classes['list-body']}
                  key={item.serviceOperation}
                >
                  <Col
                    sm={3}
                    md={3}
                    lg={3}
                    className={classes['list-ops-name']}
                  >
                    <p className={classes.item} title={item.serviceOperation} data-testid="ops-list-name">{item.serviceOperation}</p>
                  </Col>
                  <Col sm={3} md={3} lg={3} className={classes['operation-type']}>
                    <p
                      data-testid="ops-list-verbtype"
                      className={`${classes['list-ops-verb']} ${serviceType.toLocaleLowerCase()
                        === FORM_SERVICE_TYPE.DB2 ? classes['db-ops-type'] : ''}`}
                    >
                      {' '}
                      {item.operationVerb}
                    </p>
                  </Col>
                  <Col sm={2} md={2} lg={2} className={classes['square-badge']}>
                    <SquareBadge
                      data-testid="ingress-count"
                      count={
                        getAssociatedIngressEvents(
                          ingressEventMappingList,
                          item.serviceOperation
                        ).length
                      }
                    />
                  </Col>
                  <Col sm={2} md={2} lg={2} className={classes['square-badge']}>
                    <SquareBadge
                      count={
                        getAssociatedEgressEvents(
                          egressEventMappingList,
                          item.serviceOperation
                        ).length
                      }
                    />
                  </Col>
                  {isViewMode && (
                  <Col sm={2} md={2} lg={2}>
                    <CIActionBtnGroup buttons={[{ label: 'view', key: 'viewIcon', onClick: async () => onViewHandler(index), testId: 'btn-view' }]} />
                  </Col>
                  )}
                  {!isViewMode && (
                  <Col
                    sm={2}
                    md={2}
                    lg={2}
                    className={classes['edit-ops-btns']}
                  >
                    <Button
                      onClick={() => onEditClickHandler(index)}
                      data-testid="btn-edit-so"
                      disabled={isServiceOpsEditButtonClicked}
                      variant="tertiary"
                      className={classes['edit-button']}
                    >
                      <MyIcon name="editIcon" className={classes['icons-hover']} />
                    </Button>
                    <Button
                      onClick={() => confirmDeleteServiceOperation(index)}
                      data-testid="btn-delete-so"
                      disabled={
                          isDeleteServiceOperationDisabled(
                            item.serviceOperation,
                            isEditService || false,
                            ingressEventMappingList,
                            egressEventMappingList
                          ) || isServiceOpsEditButtonClicked
                        }
                      variant="tertiary"
                      className={classes['delete-button']}
                    >
                      <MyIcon name="deleteIcon" className={classes['icons-hover']} />
                    </Button>
                    {useConfirmDialogHook.render()}
                  </Col>
                  )}
                </Row>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
export default ServiceOperationList;
